import { api } from '../lib/api.js';

export type PaymentTrigger = 'ADVANCE' | 'PDR' | 'CDR' | 'FAT' | 'SAT' | 'MANUAL'
  // 11b (אלי, 24/09/2026): כל תנאי מקושר לשלב.
  | 'RELEASE' | 'DELIVERY' | 'COMPLETION'
  // U-1009-02: לוח החיוב של חוזה שירות.
  | 'CONTRACT';
export type MilestoneStatus = 'PENDING' | 'DUE' | 'INVOICED';

export const TRIGGER_LABEL: Record<PaymentTrigger, string> = {
  ADVANCE: 'מקדמה', PDR: 'PDR', CDR: 'CDR', FAT: 'FAT', SAT: 'SAT / מסירה', MANUAL: 'ידני',
  RELEASE: 'הורדה לייצור', DELIVERY: 'אספקה (משלוח)', COMPLETION: 'סיום פרויקט',
  CONTRACT: 'חוזה שירות',
};
export const STATUS_LABEL: Record<MilestoneStatus, string> = {
  PENDING: 'ממתין לאירוע', DUE: 'מוכן לחשבונית', INVOICED: 'חשבונית הופקה',
};

export type Milestone = {
  id: string;
  label: string;
  trigger: PaymentTrigger;
  percent: number | null;
  isRemainder: boolean;
  amount: number;
  /** false על שורת ₪0 — לא מגיע כלום, ולכן היא לא ממתינה לכלום. */
  applicable: boolean;
  status: MilestoneStatus;
  dueAt: string | null;
  invoicedAt: string | null;
  invoicedByName: string | null;
  invoiceRef: string | null;
  /**
   * חודש החיוב — 'YYYY-MM', null כשעוד לא הוצאה חשבונית.
   *
   * Not `invoicedAt`: that says when the button was pressed and never moves,
   * this says which month the money belongs to. An invoice issued in October
   * for September is the ordinary case, and it is what the sales target reads.
   */
  billingMonth: string | null;
  sortOrder: number;
  // ── שוחרר לחיוב לפני שהשלב נסגר ───────────────────────────────────────
  //
  // אלי, 21/09/2026: „חסרה לי אופציה להפיק חשבונית על שלב באופן ידני…
  // בלי שנסגר תהליך מסוים."
  //
  // זה מה שמבדיל חשבונית כזו מחשבונית רגילה — ולכן הוא מגיע עם כל שורה,
  // לכל מסך, ולא רק ליומן השינויים.
  /** true = מישהו שחרר ידנית; false = השער הוא שפתח. */
  releasedEarly: boolean;
  releasedAt: string | null;
  releasedByName: string | null;
  releaseReason: string | null;
  /** מתי השער נסגר בכל זאת, אחרי השחרור. null = עוד לא נסגר. */
  gateClosedAt: string | null;
};

export type ProjectBilling = {
  projectId: string;
  projectNumber: string;
  projectName: string | null;
  client: string | null;
  isInternal: boolean;
  contractValue: number | null;
  milestones: Milestone[];
  billed: number;
  due: number;
  /** ערך החוזה פחות מה שחויב — `null` כשאין ערך חוזה. `shekel` מדפיס '—'. */
  open: number | null;
};

export type TermInput = {
  /**
   * The milestone this row already IS.
   *
   * Without it the server had to guess identity from the TRIGGER, and this
   * screen defaults every added line to MANUAL — so two manual interim
   * payments where one was already invoiced came back with BOTH marked
   * invoiced, same ₪, same date, same invoice reference. Sending the id is
   * what makes a re-save mean what it looks like.
   */
  id?: string;
  label: string; trigger: PaymentTrigger; percent?: number | null; isRemainder?: boolean;
};

/**
 * שורה ב„חשבוניות" — של פרויקט, או (U-1009-02) של חוזה שירות: אז `projectId`
 * ריק, `contractId` מלא, ו-`projectNumber` הוא C-0001.
 */
export type WorklistItem = Milestone & {
  projectId: string | null; contractId: string | null;
  projectNumber: string; projectName: string | null; client: string | null;
  /** חוזה: התאריך בלוח החיוב. */
  plannedAt: string | null;
};

export type GroupDimension = 'project' | 'pm' | 'engineer' | 'customer';
export type TimeBucket = 'month' | 'quarter' | 'half' | 'year';
export type BillingGroup = {
  key: string; label: string; contract: number; billed: number; due: number; open: number; count: number;
  /** מתוך `count`, כמה פרויקטים אין להם ערך חוזה — הסכומים חסרים אותם. */
  noContractValue: number;
};
export type BillingReport = {
  totals: { contract: number; billed: number; due: number; open: number; projectCount: number; noContractValue: number };
  groups: BillingGroup[];
  timeline: { period: string; billed: number }[];
};

export const GROUP_LABEL: Record<GroupDimension, string> = { project: 'פרויקט', pm: 'מנהל פרויקט', engineer: 'מהנדס', customer: 'לקוח' };
export const BUCKET_LABEL: Record<TimeBucket, string> = { month: 'חודשי', quarter: 'רבעוני', half: 'חציוני', year: 'שנתי' };

export const billingApi = {
  forProject: (projectId: string) => api<ProjectBilling>(`/api/billing/project/${encodeURIComponent(projectId)}`),
  setTerms: (projectId: string, contractValue: number | null, terms: TermInput[]) =>
    api<ProjectBilling>(`/api/billing/project/${encodeURIComponent(projectId)}/terms`, { method: 'POST', body: { contractValue, terms } }),
  markInvoiced: (milestoneId: string, invoiceRef?: string | null, billingMonth?: string | null) =>
    api<Milestone>(`/api/billing/milestone/${encodeURIComponent(milestoneId)}/invoice`, { method: 'POST', body: { invoiceRef, billingMonth } }),

  /** להעביר חשבונית שכבר יצאה לחודש אחר — הדשבורד זז מיד, והשינוי נרשם. */
  setBillingMonth: (milestoneId: string, billingMonth: string) =>
    api<Milestone>(`/api/billing/milestone/${encodeURIComponent(milestoneId)}/billing-month`, { method: 'POST', body: { billingMonth } }),
  // reason is REQUIRED by the server when this undoes an invoice, so it is not
  // optional here either — a caller cannot forget it and meet the 400 at runtime.
  setStatus: (milestoneId: string, status: MilestoneStatus, reason?: string) =>
    api<Milestone>(`/api/billing/milestone/${encodeURIComponent(milestoneId)}/status`, { method: 'POST', body: { status, reason } }),

  /**
   * לשחרר סעיף לחיוב בלי שהשלב שלו נסגר — עם סיבה, שהיא חובה.
   *
   * ‎`reason` אינו אופציונלי כאן בכוונה: השרת דוחה בלעדיו ב-400, וקורא
   * שיכול לשכוח אותו יפגוש את זה רק בזמן ריצה. הסיבה נשמרת על השורה
   * ומוצגת לצד כל חשבונית שתצא עליה, לתמיד.
   */
  releaseForBilling: (milestoneId: string, reason: string) =>
    api<Milestone>(`/api/billing/milestone/${encodeURIComponent(milestoneId)}/release`, { method: 'POST', body: { reason } }),
  worklist: (status?: MilestoneStatus) =>
    api<WorklistItem[]>(`/api/billing/worklist${status ? `?status=${status}` : ''}`),
  report: (groupBy: GroupDimension, bucket: TimeBucket) =>
    api<BillingReport>(`/api/billing/report?groupBy=${groupBy}&bucket=${bucket}`),
};

export const shekel = (n: number | null | undefined): string =>
  n == null ? '—' : `₪${Math.round(n).toLocaleString('he-IL')}`;

// The default payment-terms template (מקדמה 30 · PDR 20 · CDR 10 · FAT 10 · יתרה).
export const DEFAULT_TERMS: TermInput[] = [
  { label: 'מקדמה', trigger: 'ADVANCE', percent: 30 },
  { label: 'PDR', trigger: 'PDR', percent: 20 },
  { label: 'CDR', trigger: 'CDR', percent: 10 },
  { label: 'FAT', trigger: 'FAT', percent: 10 },
  { label: 'SAT / יתרה', trigger: 'SAT', isRemainder: true },
];
